import React, { useEffect, useRef, useState } from 'react';
import styles from '../pages/Dashboard.module.css';
import {
  DAY_OPTIONS, WEEK_OPTIONS, recentMonths, monthKey, monthLabel, toISODate, resolveRange, selectionName,
} from '../utils/dateRange';

// Dashboard "Date Range" control (Kayd's 15.10.2026 follow-up):
//   [Days ▾] [Weeks ▾] [🗓 Calendar]
// Days/Weeks apply immediately. Calendar opens a panel with three choices
// (one month, multiple consecutive months, custom start → end) that only
// apply on "Apply", after validation, so a half-filled range never reaches
// the dashboard.
const CAL_TABS = [
  { key: 'month', label: 'Month' },
  { key: 'months', label: 'Multiple months' },
  { key: 'custom', label: 'Custom range' },
];

export default function DateRangePicker({ value, onChange, today }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('month');
  const [draft, setDraft] = useState({});
  const [error, setError] = useState('');
  const wrapRef = useRef(null);
  const months = recentMonths(today, 24);
  const todayIso = toISODate(today);
  const isCalendar = ['month', 'months', 'custom'].includes(value.mode);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openCalendar = () => {
    if (open) { setOpen(false); return; }
    const current = monthKey(today);
    setTab(isCalendar ? value.mode : 'month');
    setDraft({
      month: value.month || current,
      fromMonth: value.fromMonth || months[2] || current,
      toMonth: value.toMonth || current,
      from: value.from || toISODate(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: value.to || todayIso,
    });
    setError('');
    setOpen(true);
  };

  const apply = () => {
    let sel;
    if (tab === 'month') sel = { mode: 'month', month: draft.month };
    else if (tab === 'months') sel = { mode: 'months', fromMonth: draft.fromMonth, toMonth: draft.toMonth };
    else sel = { mode: 'custom', from: draft.from, to: draft.to };

    if (tab === 'months' && draft.fromMonth > draft.toMonth) return setError('The first month must not be after the last month.');
    if (tab === 'custom') {
      if (!draft.from || !draft.to) return setError('Please choose both a start date and an end date.');
      if (draft.from > draft.to) return setError('The start date must not be after the end date.');
      if (draft.to > todayIso) return setError('The end date cannot be in the future.');
    }
    if (!resolveRange(sel, today)) return setError('Please choose a valid period.');
    onChange(sel);
    setOpen(false);
  };

  const set = (k) => (e) => { setDraft((d) => ({ ...d, [k]: e.target.value })); setError(''); };

  return (
    <div className={styles.rangeBox} ref={wrapRef}>
      <span className={styles.rangeLabel}>Date Range</span>
      <div className={styles.rangeControls} role="group" aria-label="Date range">
        <select
          aria-label="Days"
          className={`${styles.rangeSelect} ${value.mode === 'days' ? styles.rangeActive : ''}`}
          value={value.mode === 'days' ? String(value.n) : ''}
          onChange={(e) => { if (e.target.value) { onChange({ mode: 'days', n: Number(e.target.value) }); setOpen(false); } }}
        >
          <option value="" disabled>Days</option>
          {DAY_OPTIONS.map((n) => <option key={n} value={n}>{n === 1 ? '1 day' : `${n} days`}</option>)}
        </select>
        <select
          aria-label="Weeks"
          className={`${styles.rangeSelect} ${value.mode === 'weeks' ? styles.rangeActive : ''}`}
          value={value.mode === 'weeks' ? String(value.n) : ''}
          onChange={(e) => { if (e.target.value) { onChange({ mode: 'weeks', n: Number(e.target.value) }); setOpen(false); } }}
        >
          <option value="" disabled>Weeks</option>
          {WEEK_OPTIONS.map((n) => <option key={n} value={n}>{n === 1 ? '1 week' : `${n} weeks`}</option>)}
        </select>
        <button
          type="button"
          className={`${styles.rangeSelect} ${styles.rangeCalBtn} ${isCalendar ? styles.rangeActive : ''}`}
          onClick={openCalendar}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          🗓 {isCalendar ? selectionName(value) : 'Calendar'}
        </button>
      </div>

      {open && (
        <div className={styles.calPanel} role="dialog" aria-label="Choose a period">
          <div className={styles.calTabs} role="tablist">
            {CAL_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`${styles.calTab} ${tab === t.key ? styles.calTabActive : ''}`}
                onClick={() => { setTab(t.key); setError(''); }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'month' && (
            <label className={styles.calField}>
              <span>Select a month</span>
              <select value={draft.month} onChange={set('month')}>
                {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
            </label>
          )}

          {tab === 'months' && (
            <div className={styles.calRow}>
              <label className={styles.calField}>
                <span>From month</span>
                <select value={draft.fromMonth} onChange={set('fromMonth')}>
                  {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
              </label>
              <span className={styles.calArrow} aria-hidden="true">→</span>
              <label className={styles.calField}>
                <span>To month</span>
                <select value={draft.toMonth} onChange={set('toMonth')}>
                  {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
              </label>
            </div>
          )}

          {tab === 'custom' && (
            <div className={styles.calRow}>
              <label className={styles.calField}>
                <span>Start date</span>
                <input type="date" value={draft.from} max={todayIso} onChange={set('from')} />
              </label>
              <span className={styles.calArrow} aria-hidden="true">→</span>
              <label className={styles.calField}>
                <span>End date</span>
                <input type="date" value={draft.to} max={todayIso} onChange={set('to')} />
              </label>
            </div>
          )}

          {error && <p className={styles.calError} role="alert">{error}</p>}

          <div className={styles.calActions}>
            <button type="button" className={styles.calCancel} onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className={styles.calApply} onClick={apply}>Apply</button>
          </div>
        </div>
      )}
    </div>
  );
}
